'use client';

import {
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  Heading,
  FilterGroup,
  FilterHeader,
  Select,
  InputChips,
  type SelectItem,
  Text,
  Separator,
} from '@/_components';
import type { OverviewFiltersProps } from './OverviewFilters.types';
import type { OverviewFilterObject } from '@/_types/overview';
import { ColumnEditor } from './components/ColumnEditor';
import { LayoutContext } from '@/_contexts/LayoutProvider';
import { OverviewContext } from '@/_contexts/admin/OverviewProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { getAllQuestionnaireYears } from '@/_api/admin-questionnaires';
import { TAX_YEARS } from '@/_constants/year-options';
import {
  useCallback,
  useEffect,
  useState,
  useContext,
  type FC,
  type ChangeEvent,
  useMemo,
} from 'react';
import { ALL_ENTITY_TYPE_ITEMS } from '@/_constants/entities';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { EntityV2Context } from '@/_contexts/admin/EntityV2Provider';
import { areArraysEqual } from '@/_utils/array';

const ALL_YEAR_ITEM: SelectItem = { name: 'All', value: 'All' };
const DEFAULT_YEAR_ITEMS: Array<SelectItem> = [ALL_YEAR_ITEM, ...TAX_YEARS];

const QUESTIONNAIRE_TYPES: Array<SelectItem> = [
  { name: 'All', value: 'All' },
  { name: 'Templates', value: 'Templates' },
  { name: 'Custom', value: 'Custom' },
];

const QUESTIONNAIRE_STATUSES: Array<SelectItem> = [
  { name: 'All', value: 'All' },
  { name: 'Pending', value: 'pending' },
  { name: 'Sent', value: 'sent' },
  { name: 'Opened', value: 'opened' },
  { name: 'In Progress', value: 'in_progress' },
  { name: 'Completed', value: 'completed' },
  { name: 'Draft', value: 'draft' },
];

export const OverviewFilters: FC<OverviewFiltersProps> = () => {
  const [taxYears, setTaxYears] =
    useState<Array<SelectItem>>(DEFAULT_YEAR_ITEMS);
  const [isColumnEditor, setColumnEditor] = useState<boolean>(false);
  const [selectedFirmRole, setSelectedFirmRole] = useState<string>('');
  const [selectedFirmUser, setSelectedFirmUser] = useState<string>('');

  const { isAssistantOpen, setFilterContent } = useContext(LayoutContext);
  const { token, isFull, isPartner } = useContext(UserContext);
  const { filter, setFilter, setPagination } = useContext(OverviewContext);
  const {
    preferences,
    firmUserOptions,
    firmRoleOptions,
    firmGroupQuestionnaireOptions,
    clientTagOptions,
    entityTagOptions,
    firmCustomStatuses,
    firmEntityTypes,
  } = useContext(FirmSettingsContext);
  const { onToggleClientTags } = useContext(CompanyContext);
  const { toggleEntityTagsDialog } = useContext(EntityV2Context);

  const setFirstPageFilter = useCallback(
    (filter: OverviewFilterObject) => {
      setFilter({ ...filter, page: 0 });
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [setPagination, setFilter],
  );

  const onToggleColumnEditor = () => setColumnEditor(!isColumnEditor);

  const [entityChips, setEntityChips] = useState<Set<string>>(
    new Set(filter.selected_entity_tags_filter || []),
  );
  const [entityChipText, setEntityChipText] = useState<string>('');
  // const [isEntityTagActive, setIsEntityTagActive] = useState<boolean>(false);

  const [clientChips, setClientChips] = useState<Set<string>>(
    new Set(filter.selected_client_tags_filter || []),
  );
  const [clientChipText, setClientChipText] = useState<string>('');
  // const [isClientTagActive, setIsClientTagActive] = useState<boolean>(false);

  const [statusChips, setStatusChips] = useState<Set<string>>(
    new Set(
      filter?.selected_firm_custom_status_filter?.map((s) => s.toString()) ||
        [],
    ),
  );
  const [statusChipText, setStatusChipText] = useState<string>('');

  const onEntityChipText = (event: ChangeEvent<HTMLInputElement>) =>
    setEntityChipText(event.target.value);

  const onClientChipText = (event: ChangeEvent<HTMLInputElement>) =>
    setClientChipText(event.target.value);

  const onStatusChipText = (event: ChangeEvent<HTMLInputElement>) =>
    setStatusChipText(event.target.value);

  useEffect(() => {
    if (
      !areArraysEqual(
        Array.from(entityChips),
        filter.selected_entity_tags_filter,
      )
    ) {
      // Update filter when entity chips change
      setFirstPageFilter({
        ...filter,
        selected_entity_tags_filter: Array.from(entityChips),
      });
    }
  }, [entityChips, filter, setFirstPageFilter]);

  useEffect(() => {
    if (
      !areArraysEqual(
        Array.from(clientChips),
        filter.selected_client_tags_filter,
      )
    ) {
      // Update filter when client chips change
      setFirstPageFilter({
        ...filter,
        selected_client_tags_filter: Array.from(clientChips),
      });
    }
  }, [clientChips, filter, setFirstPageFilter]);

  useEffect(() => {
    const hasValidFirmRoleState: boolean =
      filter?.firm_role_id !== -1 &&
      filter?.firm_role_user_id !== -1 &&
      typeof filter?.firm_role_id === 'number' &&
      typeof filter?.firm_role_user_id === 'number';

    if (hasValidFirmRoleState) {
      setSelectedFirmRole(filter?.firm_role_id?.toString() || '');
      setSelectedFirmUser(filter?.firm_role_user_id?.toString() || '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const parsedStatusChips: Array<number> = Array.from(statusChips)?.map(
      (chip) => parseInt(chip),
    );
    const parsedSelectedFilter: Array<number> =
      filter.selected_firm_custom_status_filter || [];

    if (!areArraysEqual(parsedStatusChips, parsedSelectedFilter)) {
      setFirstPageFilter({
        ...filter,
        selected_firm_custom_status_filter: parsedStatusChips,
      });
    }
  }, [statusChips, filter, setFirstPageFilter]);

  useEffect(() => {
    const getYears = async () => {
      const { years } = (await getAllQuestionnaireYears({ token })) as any;
      const newYearItems: Array<SelectItem> = [
        ALL_YEAR_ITEM,
        ...years.map((y: number) => ({
          name: y.toString(),
          value: y.toString(),
        })),
      ];

      setTaxYears(newYearItems);
    };

    token && getYears();
  }, [token]);

  const allEntityTypes: Array<SelectItem> = useMemo(() => {
    if (firmEntityTypes.length === 0) return ALL_ENTITY_TYPE_ITEMS;

    return [
      { name: 'All Entity Types', value: 'All' },
      ...firmEntityTypes.map((t: string) => ({
        name: t,
        value: t,
      })),
    ];
  }, [firmEntityTypes]);

  const onClose = () => setFilterContent(null);
  const onFilterChange = (
    newFilter: Partial<OverviewFilterObject | object>,
  ) => {
    const newFilters: OverviewFilterObject = {
      ...filter,
      ...newFilter,
    };

    return setFirstPageFilter(newFilters);
  };

  const onYearChange = (event: ChangeEvent<HTMLSelectElement>) =>
    onFilterChange({ year: event.target.value });

  const onEntityTypeChange = (event: ChangeEvent<HTMLSelectElement>) =>
    onFilterChange({ entity_type: event.target.value });

  const onQuestionnaireTypeChange = (event: ChangeEvent<HTMLSelectElement>) =>
    onFilterChange({ questionnaire_type: event.target.value });

  const onQuestionnaireStatusChange = (event: ChangeEvent<HTMLSelectElement>) =>
    onFilterChange({ questionnaire_status: event.target.value });

  const onToggleLocked = (_event: ChangeEvent<HTMLInputElement>) =>
    onFilterChange({
      questionnaire_status_include_locked:
        !filter.questionnaire_status_include_locked,
    });

  const onToggleCompleted = (_event: ChangeEvent<HTMLInputElement>) =>
    onFilterChange({
      questionnaire_status_exclude_completed:
        !filter.questionnaire_status_exclude_completed,
    });

  const onToggleInactiveEntities = (_event: ChangeEvent<HTMLInputElement>) =>
    onFilterChange({
      hide_actives: false,
      hide_inactives: !filter.hide_inactives,
    });

  const onToggleActiveEntities = (_event: ChangeEvent<HTMLInputElement>) =>
    onFilterChange({
      hide_actives: !filter.hide_actives,
      hide_inactives: false,
    });

  const onToggleInactiveUsers = (_event: ChangeEvent<HTMLInputElement>) =>
    onFilterChange({
      hide_active_users: false,
      hide_inactive_users: !filter.hide_inactive_users,
    });

  const onToggleActiveUsers = (_event: ChangeEvent<HTMLInputElement>) =>
    onFilterChange({
      hide_active_users: !filter.hide_active_users,
      hide_inactive_users: false,
    });

  const onToggleDeleted = (_event: ChangeEvent<HTMLInputElement>) =>
    onFilterChange({
      show_only_deleted: !filter.show_only_deleted,
    });

  const onQuestionnaireFirmGroupChange = (
    event: ChangeEvent<HTMLSelectElement>,
  ) =>
    onFilterChange({
      firm_group_id: parseInt(event.target.value),
    });

  const onQuestionnaireFirmRoleChange = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    const firmRoleId: number = parseInt(event.target.value) || -1;

    setSelectedFirmRole(event.target.value);
    onFilterChange({
      firm_role_id: firmRoleId,
    });
  };

  const onQuestionnaireFirmUserChange = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    const firmRoleUserId: number = parseInt(event.target.value) || -1;

    setSelectedFirmUser(event.target.value);
    onFilterChange({
      firm_role_user_id: firmRoleUserId,
    });
  };

  const selectedFirmRoleName: string | undefined = firmRoleOptions?.find(
    (firmRole) => firmRole.value === selectedFirmRole,
  )?.name;

  const selectedFirmUserName: string | undefined = firmUserOptions?.find(
    (firmRole) => firmRole.value === selectedFirmUser,
  )?.name;

  const allCustomStatusOptions: Array<SelectItem> = useMemo(() => {
    const statuses: Array<SelectItem> = firmCustomStatuses.map((status) => ({
      name: status.firm_user_display_status,
      value: status.id.toString(),
    }));

    const statusesWithNone: Array<SelectItem> = [
      { name: 'No Status', value: '-1' },
      ...statuses,
    ];

    return statusesWithNone;
  }, [firmCustomStatuses]);

  const onQuestionnaireAssignedFirmUserChange = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    onFilterChange({
      assigned_advisor_id: parseInt(event.target.value),
    });
  };

  const onResetFirmRole = () => {
    setSelectedFirmRole('');
    setSelectedFirmUser('');

    return onFilterChange({
      firm_role_id: -1,
      firm_role_user_id: -1,
    });
  };

  const onResetFirmUser = () => {
    setSelectedFirmUser('');

    return onFilterChange({
      firm_role_user_id: -1,
    });
  };

  return (
    <Flex isColumn={true}>
      <FilterHeader>
        <Heading level={4}>Filters</Heading>
        <ButtonGroup>
          {isColumnEditor ? (
            <Button
              intent="ghost"
              value="Back"
              size="small"
              iconLeft="arrow-left"
              onClick={onToggleColumnEditor}
            />
          ) : (
            <Button
              intent="ghost"
              size="small"
              value="Columns"
              isDisabled={isAssistantOpen}
              withTooltip={isAssistantOpen}
              tooltip={
                isAssistantOpen
                  ? 'Please close the Admin Assistant to update columns'
                  : undefined
              }
              iconLeft="sliders"
              onClick={onToggleColumnEditor}
            />
          )}
          <Button
            value="Close"
            isIconOnly={true}
            iconLeft="x"
            size="small"
            onClick={onClose}
            intent="ghost"
          />
        </ButtonGroup>
      </FilterHeader>
      {isColumnEditor ? (
        <ColumnEditor />
      ) : (
        <Flex isColumn={true} padding={8} gap={8}>
          <Select
            label="Year"
            isFullWidth={true}
            selectedValue={filter.year.toString()}
            onChange={onYearChange}
            items={taxYears}
          />
          <Select
            label="Entity Type"
            isFullWidth={true}
            selectedValue={filter.entity_type}
            onChange={onEntityTypeChange}
            items={allEntityTypes}
          />
          <Select
            label="Questionnaire Type"
            isFullWidth={true}
            selectedValue={filter.questionnaire_type}
            onChange={onQuestionnaireTypeChange}
            items={QUESTIONNAIRE_TYPES}
          />
          <Select
            label="Questionnaire Status"
            isFullWidth={true}
            selectedValue={filter.questionnaire_status.toString()}
            onChange={onQuestionnaireStatusChange}
            items={QUESTIONNAIRE_STATUSES}
          />
          {preferences?.custom_statuses_enabled && (
            <InputChips
              results={allCustomStatusOptions}
              label="Firm-Internal Status"
              value={statusChipText}
              onChange={onStatusChipText}
              chips={statusChips}
              chipNames={allCustomStatusOptions}
              setChips={setStatusChips}
              placeholder="Select Internal Status Filter"
            />
          )}
          <Flex isColumn={true} gap={4} isFullWidth={true}>
            {/* Entity Tag filter */}
            <InputChips
              results={entityTagOptions}
              chips={entityChips}
              label="Entity Tag"
              onChange={onEntityChipText}
              setChips={setEntityChips}
              value={entityChipText}
              placeholder="Select or Add Entity Tags"
            />
            <ButtonGroup>
              <Button
                onClick={() => {
                  toggleEntityTagsDialog();
                  onClose();
                }}
                iconLeft="pencil"
                value="Edit Entity Tags"
                size="tiny"
                intent="ghost"
              />
            </ButtonGroup>
          </Flex>

          <Flex isColumn={true} gap={4}>
            {/* User Tag filter */}
            <InputChips
              results={clientTagOptions}
              chips={clientChips}
              label="User Tag"
              onChange={onClientChipText}
              setChips={setClientChips}
              value={clientChipText}
              placeholder="Select or Add User Tags"
            />
            <ButtonGroup>
              <Button
                onClick={() => {
                  onToggleClientTags();
                  onClose();
                }}
                iconLeft="pencil"
                value="Edit User Tags"
                size="tiny"
                intent="ghost"
              />
            </ButtonGroup>
          </Flex>
          <Select
            isFullWidth={true}
            label="Assigned Firm User"
            selectedValue={filter.assigned_advisor_id?.toString?.() || '-1'}
            onChange={onQuestionnaireAssignedFirmUserChange}
            items={[{ name: 'None', value: '-1' }, ...firmUserOptions]}
          />
          {preferences?.has_firm_groups && (isFull || isPartner) && (
            <Select
              label="Firm Group"
              placeholder="Select firm group"
              isFullWidth={true}
              selectedValue={filter.firm_group_id.toString()}
              onChange={onQuestionnaireFirmGroupChange}
              items={firmGroupQuestionnaireOptions}
            />
          )}
          {preferences?.use_firm_roles && (
            <FilterGroup
              roleName={selectedFirmRoleName}
              userName={selectedFirmUserName}
              onResetRole={onResetFirmRole}
              onResetUser={onResetFirmUser}
            >
              <Flex isColumn={true} gap={8}>
                {!selectedFirmRole ? (
                  <Select
                    label="Firm Role"
                    placeholder="Select firm role"
                    items={firmRoleOptions}
                    selectedValue={selectedFirmRole}
                    onChange={onQuestionnaireFirmRoleChange}
                  />
                ) : (
                  <Select
                    label="Firm User"
                    placeholder="Select firm user"
                    items={firmUserOptions}
                    selectedValue={selectedFirmUser}
                    onChange={onQuestionnaireFirmUserChange}
                  />
                )}
              </Flex>
            </FilterGroup>
          )}
          <Separator />
          <Flex isColumn={true} gap={6}>
            <Flex isColumn={true} gap={2}>
              <Text type="small" color="primary" fontWeight="medium">
                Questionnaire
              </Text>
              <Checkbox
                isChecked={filter.questionnaire_status_include_locked}
                onChange={onToggleLocked}
                label="Include Locked"
                isSmall={true}
              />
              <Checkbox
                isChecked={filter.questionnaire_status_exclude_completed}
                onChange={onToggleCompleted}
                label="Exclude Completed"
                isSmall={true}
              />
              <Checkbox
                isChecked={filter.show_only_deleted ?? false}
                onChange={onToggleDeleted}
                label="Show Only Deleted"
                isSmall={true}
              />
            </Flex>
            <Flex isColumn={true} gap={2}>
              <Text type="small" color="primary" fontWeight="medium">
                Entities
              </Text>
              <Checkbox
                isChecked={filter.hide_inactives}
                isDisabled={filter.hide_actives}
                onChange={onToggleInactiveEntities}
                label="Hide Inactive"
                isSmall={true}
              />
              <Checkbox
                isChecked={filter.hide_actives}
                isDisabled={filter.hide_inactives}
                onChange={onToggleActiveEntities}
                label="Hide Active"
                isSmall={true}
              />
            </Flex>
            <Flex isColumn={true} gap={2}>
              <Text type="small" color="primary" fontWeight="medium">
                Users
              </Text>
              <Checkbox
                isChecked={filter.hide_inactive_users}
                isDisabled={filter.hide_active_users}
                onChange={onToggleInactiveUsers}
                label="Hide Inactive"
                isSmall={true}
                helperText="Includes testing users"
              />
              <Checkbox
                isChecked={filter.hide_active_users}
                isDisabled={filter.hide_inactive_users}
                onChange={onToggleActiveUsers}
                label="Hide Active"
                isSmall={true}
              />
            </Flex>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
